import React from "react";
import { motion } from "framer-motion";
import { ConversationViewProps } from "./interfaces";

export const ConversationView: React.FC<ConversationViewProps> = ({
  conversation,
}) => {
  // Function to get message style based on speaker position
  const getMessageStyle = (index: number, totalMessages: number) => {
    // First message is always on the left
    if (index === 0) return false;
    // Alternate between left and right for better readability
    return index % 2 === 0;
  };

  return (
    <div className="flex flex-col gap-6 p-6 min-h-[400px]">
      {conversation.map((message, index) => {
        const isRight = getMessageStyle(index, conversation.length);
        // Handle different message formats - some might use speaker/dialogue, others might use role/text
        const speaker = message.speaker || message.role || "Unknown";
        const dialogue = message.dialogue || message.text || "";
        const context = message.context || ""; // This might not exist in all conversation formats

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className={`flex ${isRight ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`
                relative group max-w-[80%] rounded-2xl p-4 
                ${
                  isRight
                    ? "bg-gradient-to-br from-[#0A84FF] to-[#0071E3] text-white ml-auto shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30"
                    : "bg-gradient-to-br from-[#F2F2F7] to-[#E5E5EA] text-[#1D1D1F] shadow-md hover:shadow-lg"
                }
                transform transition-all duration-200 hover:scale-[1.02] hover:-translate-y-0.5
              `}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`flex items-center gap-2 ${
                    isRight ? "text-white/90" : "text-[#1D1D1F]"
                  }`}
                >
                  <span className="font-semibold text-sm">
                    {speaker}
                  </span>
                  {context && (
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        isRight
                          ? "bg-white/20 text-white/90"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {context}
                    </span>
                  )}
                </div>
              </div>
              <p
                className={`text-[15px] leading-relaxed tracking-normal ${
                  isRight ? "text-white/90" : "text-[#1D1D1F]"
                }`}
              >
                {dialogue}
              </p>
              <div
                className={`absolute ${
                  isRight ? "-left-2" : "-right-2"
                } bottom-0 w-4 h-4 transform ${
                  isRight ? "rotate-45" : "-rotate-45"
                } ${isRight ? "bg-[#0071E3]" : "bg-[#E5E5EA]"}`}
              />
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}; 